import SoundGate from "@/components/SoundGate";
import NavPane from "@/components/NavPane";
import { YoutubeIcon, SoundcloudIcon, TwitterXIcon, LinkedInIcon, GithubIcon, FacebookIcon } from "@/components/SocialIcons";

const SocialLink = ({ href, label, children, testId }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    aria-label={label}
    title={label}
    className="inline-flex items-center justify-center w-10 h-10 rounded-sm border transition-all"
    style={{ borderColor: "rgba(229,200,112,0.3)", color: "#E5C870" }}
    onMouseEnter={(e) => { e.currentTarget.style.color = "#F5D98A"; e.currentTarget.style.borderColor = "rgba(229,200,112,0.7)"; }}
    onMouseLeave={(e) => { e.currentTarget.style.color = "#E5C870"; e.currentTarget.style.borderColor = "rgba(229,200,112,0.3)"; }}
    data-testid={testId}
  >
    {children}
  </a>
);

const MainMenu = () => {
  return (
    <SoundGate theme="studio">
      <div
        className="studio-home relative min-h-screen overflow-x-clip"
        data-testid="main-menu"
        style={{ background: "#0C0A08", color: "#F5F1E8" }}
      >
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 55% 40% at 22% 18%, rgba(229,200,112,0.09), transparent 65%), radial-gradient(ellipse 60% 45% at 82% 82%, rgba(122,31,31,0.13), transparent 70%)",
          }}
        />
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.035] mix-blend-overlay"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
          }}
        />

        <div className="relative z-10 min-h-screen flex flex-col md:flex-row max-w-7xl mx-auto">
          {/* LEFT NAV PANE */}
          <div
            className="md:w-[260px] md:shrink-0 md:min-h-screen flex flex-col pt-4 md:pt-6 px-4 md:px-6 pb-6 md:border-r"
            style={{ borderColor: "rgba(229,200,112,0.14)" }}
            data-testid="home-left-pane"
          >
            <NavPane theme="studio" />
          </div>

          {/* MAIN COLUMN */}
          <main className="flex-1 flex flex-col pt-4 md:pt-6 px-6 md:px-12 pb-10">
            <div className="max-w-3xl">
              <h1
                className="leading-[1.02] mb-3"
                style={{
                  fontFamily: "'Times New Roman', Times, serif",
                  fontWeight: 500,
                  color: "#E5C870",
                  fontSize: "clamp(2.4rem, 5vw, 5rem)",
                  letterSpacing: "0.005em",
                  textShadow: "0 2px 0 rgba(0,0,0,0.55), 0 0 28px rgba(229,200,112,0.22)",
                }}
                data-testid="game-title"
              >
                The official website of Andrew Green
              </h1>

              <h2
                className="mb-7"
                style={{
                  fontFamily: "'Times New Roman', Times, serif",
                  fontWeight: 400,
                  fontStyle: "italic",
                  color: "#C9B98C",
                  fontSize: "clamp(1.15rem, 1.8vw, 1.6rem)",
                  letterSpacing: "0.01em",
                  lineHeight: 1.35,
                  textShadow: "0 1px 0 rgba(0,0,0,0.45)",
                }}
                data-testid="game-subtitle"
              >
                Welcome to my website!
              </h2>

              <p
                className="max-w-3xl mb-6 text-base"
                style={{
                  fontFamily: "'Times New Roman', Times, serif",
                  fontStyle: "normal",
                  fontWeight: 400,
                  color: "#BAB09A",
                  lineHeight: 1.65,
                }}
                data-testid="tagline"
              >
                Welcome to my homepage. I&rsquo;m a hobbyist game developer, art, UI/UX, music,
                and sound director (I get especially compulsive with art),
                AI-for-games prompt engineer, and a bit of a narrative and dialogue writer.
              </p>

              <p
                className="max-w-3xl mb-10 text-base"
                style={{
                  fontFamily: "'Times New Roman', Times, serif",
                  fontStyle: "normal",
                  fontWeight: 400,
                  color: "#BAB09A",
                  lineHeight: 1.65,
                }}
                data-testid="website-note-paragraph"
              >
                One more thing about the website: if you don&rsquo;t like some of the design
                decisions on the website, hit me up.  Don&rsquo;t like the
                code?  Help me develop this.  Got better art ideas for the gallery in the tavern,
                offer help (it&rsquo;s AI generated anyway).  Don&rsquo;t like the music?  You
                better know a composer who can take over.  Otherwise, we can just chat in the
                tavern about games, music, sound design, maybe even programming.
              </p>

              {/* ===== Announcements sub-canvas — un-numbered per user iter 52 ===== */}
              <section
                className="mb-10 p-5 rounded-sm border"
                style={{
                  borderColor: "rgba(229,200,112,0.18)",
                  background: "rgba(255,255,255,0.015)",
                }}
                data-testid="announcements-canvas"
              >
                <div
                  className="text-lg mb-3"
                  style={{
                    color: "#E5C870",
                    fontFamily: "'Times New Roman', Times, serif",
                    fontWeight: 700,
                    letterSpacing: 0,
                    textTransform: "none",
                  }}
                  data-testid="announcements-title"
                >
                  Announcements
                </div>
                <div
                  className="flex flex-col gap-5"
                  data-testid="announcements-entries"
                >
                  <article data-testid="announcements-entry-1">
                    <div
                      style={{
                        color: "#E5C870",
                        fontFamily: "'Times New Roman', Times, serif",
                        fontSize: "0.875rem",
                        letterSpacing: "0.04em",
                        marginBottom: "0.5rem",
                      }}
                      data-testid="announcements-entry-1-date"
                    >
                      May 15, 2026, 1:31am PT
                    </div>
                    <p
                      style={{
                        color: "#BAB09A",
                        lineHeight: 1.7,
                        fontFamily: "'Times New Roman', Times, serif",
                        fontWeight: 400,
                        fontSize: "1rem",
                        margin: 0,
                      }}
                      data-testid="announcements-entry-1-body"
                    >
                      I am seriously considering shifting my downtime focus from developing
                      <i style={{ fontFamily: "'Times New Roman', Times, serif" }}>{" Angus "}</i>
                      to doing playtesting for other people.  I noticed some things in
                      <i style={{ fontFamily: "'Times New Roman', Times, serif" }}>{" World of Warcraft "}</i>
                      that do not make sense and would like to report them.  I&rsquo;m
                      going to hit UpWork and see what opportunities are on that platform.
                      I&rsquo;m going to visit Game Dev Network and Game Dev League&rsquo;s
                      Discord servers and see
                      if there is anything there for me.  The reality is that this is a reaction
                      to my current predicament as an aspiring contributor to the video game dev
                      industry; my current job brings me no joy and compromises my real value
                      as an individual.  Therefore, I have to press the pedal to the metal to
                      see if I can eventually float as a playtester / QA person so that I can
                      be more at peace with my position.
                    </p>
                  </article>
                  <article data-testid="announcements-entry-0">
                    <div
                      style={{
                        color: "#E5C870",
                        fontFamily: "'Times New Roman', Times, serif",
                        fontSize: "0.875rem",
                        letterSpacing: "0.04em",
                        marginBottom: "0.5rem",
                      }}
                      data-testid="announcements-entry-0-date"
                    >
                      May 12, 2026, 12:14pm PT
                    </div>
                    <p
                      style={{
                        color: "#BAB09A",
                        lineHeight: 1.7,
                        fontFamily: "'Times New Roman', Times, serif",
                        fontWeight: 400,
                        fontSize: "1rem",
                        margin: 0,
                      }}
                      data-testid="announcements-entry-0-body"
                    >
                      I am making progress on developing
                      <i style={{ fontFamily: "'Times New Roman', Times, serif" }}>{" Angus and the Music Pantheon"}</i>.
                      I&rsquo;ve added the flowchart and a script with
                      the narrative and the dialogue for all the characters,
                      and it is a hoot.  I&rsquo;m thinking of realizing this
                      game using Gamemaker.
                    </p>
                  </article>
                </div>
              </section>
            </div>

            {/* ===== Footer — social icons row, email line below ===== */}
            <div
              className="pt-6 mt-4 border-t"
              style={{ borderColor: "rgba(229,200,112,0.14)" }}
            >
              <div className="flex items-center gap-3 mb-3">
                <SocialLink href="https://www.youtube.com/@andrewgreen1970" label="YouTube" testId="youtube-link">
                  <YoutubeIcon size={18} />
                </SocialLink>
                <SocialLink href="https://soundcloud.com/andrewgreenofficial" label="SoundCloud" testId="soundcloud-link">
                  <SoundcloudIcon size={18} />
                </SocialLink>
                <SocialLink href="https://x.com/andrewgreenoff" label="X (Twitter)" testId="twitter-link">
                  <TwitterXIcon size={16} />
                </SocialLink>
                <SocialLink href="https://www.linkedin.com/in/andrew-green-572193146/" label="LinkedIn" testId="linkedin-link">
                  <LinkedInIcon size={18} />
                </SocialLink>
                <SocialLink href="https://github.com/andrewgreen76/" label="GitHub" testId="github-link">
                  <GithubIcon size={18} />
                </SocialLink>
                <SocialLink href="https://www.facebook.com/andrewgreenunofficial" label="Facebook" testId="facebook-link">
                  <FacebookIcon size={18} />
                </SocialLink>
              </div>
              <a
                href="mailto:andrewgreenofficial@gmail.com"
                className="lowercase"
                style={{ color: "#E5C870", fontFamily: "'Times New Roman', Times, serif", textTransform: "lowercase", fontSize: "12pt", letterSpacing: "0.04em" }}
                onMouseEnter={(e) => (e.currentTarget.style.color = "#F5D98A")}
                onMouseLeave={(e) => (e.currentTarget.style.color = "#E5C870")}
                data-testid="email-link"
              >
                andrewgreenofficial@gmail.com
              </a>
            </div>
          </main>
        </div>
      </div>
    </SoundGate>
  );
};

export default MainMenu;
